import {FC, useState} from 'react';
//formik
import {Field, useFormikContext} from 'formik';
//form configuration
import {SignUpFormValues} from '../../formConfiguration';
//mui
import {Box, IconButton, InputAdornment, Link, Typography} from '@mui/material';
//constants
import {CONTINUE, END, FIELD, FIELD_LABEL, PASSWORD, PLACEHOLDER, PRIMARY, TEXT} from '@/utils/constants';
//icons
import {ReactComponent as ArrowIcon} from '@/assets/icons/arrow-line-left.svg';
import {ReactComponent as EyeSlashIcon} from '@/assets/icons/eye-slash.svg';
import {ReactComponent as EyeIcon} from '@/assets/icons/eye.svg';
//components
import Button from '@/shared/components/Button';
import Input from '@/shared/components/Inputs/Input';
import Checkbox from '@/shared/components/Checkbox';
//enums
import {ROUTER_URL} from '@/utils/enums';
//helpers
import {setRequiredLabel} from '@/utils/helpers';
//styles
import {formContentStyle} from './FormContent.styles';

interface FormContentProps {
	socialProvider?: 'Google' | 'Apple';
	socialEmail?: string;
}

const FormContent: FC<FormContentProps> = ({socialProvider, socialEmail}) => {
  const {submitForm, isSubmitting} = useFormikContext<SignUpFormValues>();

  const onSubmitClickHandler = () => submitForm();

  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const privacyPolicyTitle = (
    <Box sx={formContentStyle.privacy}>
      I accept the <Link href={ROUTER_URL.ORGANIZATION_TERMS_AND_CONDITIONS}>Terms of Use</Link> and the
      <Link href={ROUTER_URL.ORGANIZATION_PRIVACY_POLICY}>Privacy Policy</Link>
    </Box>
  );

  return (
    <Box component="form" autoComplete="off" sx={formContentStyle.form}>

      <Field
        id={FIELD.name}
        name={FIELD.name}
        placeholder={PLACEHOLDER.organizationName}
        label={setRequiredLabel(FIELD_LABEL.organizationName)}
        component={Input}
      />

      {socialProvider ? (
        <Typography sx={formContentStyle.socialEmail}>
          {socialEmail ? `Signed in as ${socialEmail}` : `Signed in with ${socialProvider}`}
        </Typography>
      ) : (
        <Field
          id={FIELD.email}
          name={FIELD.email}
          placeholder={PLACEHOLDER.email}
          label={setRequiredLabel(FIELD_LABEL.email)}
          component={Input}
        />
      )}

      <Field
        id={FIELD.address}
        name={FIELD.address}
        placeholder={PLACEHOLDER.organizationAddress}
        label={setRequiredLabel(FIELD_LABEL.organizationAddress)}
        component={Input}
      />

        <Field
        id={FIELD.city}
        name={FIELD.city}
        placeholder={PLACEHOLDER.city}
        label={setRequiredLabel(FIELD_LABEL.city)}
        component={Input}
      />
       <Field
        id={FIELD.state}
        name={FIELD.state}
        placeholder={PLACEHOLDER.state}
        label={setRequiredLabel(FIELD_LABEL.state)}
        component={Input}
      />
       <Field
        id={FIELD.zip}
        name={FIELD.zip}
        placeholder={PLACEHOLDER.zip}
        label={setRequiredLabel(FIELD_LABEL.zip)}
        component={Input}
      />

      <Field
        id={FIELD.taxIdNumber}
        name={FIELD.taxIdNumber}
        placeholder={PLACEHOLDER.taxIdNumber}
        label={FIELD_LABEL.taxIdNumber}
        component={Input}
        autoComplete="off"
      />

      {!socialProvider && (
        <>
          <input
            type="text"
            name="fake_user"
            autoComplete="username"
            style={{position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0}}
            tabIndex={-1}
            aria-hidden="true"
          />

          <Field
            id={FIELD.password}
            name={FIELD.password}
            placeholder={PLACEHOLDER.password}
            label={setRequiredLabel(FIELD_LABEL.password)}
            component={Input}
            type={showPassword ? TEXT : PASSWORD}
            sx={formContentStyle.passwordIcon}
            InputProps={{
              endAdornment: (
                <InputAdornment position={END}>
                  <IconButton onClick={handleClickShowPassword} edge={END}>
                    {showPassword ? <EyeIcon /> : <EyeSlashIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Field
            id={FIELD.confirmPassword}
            name={FIELD.confirmPassword}
            placeholder={PLACEHOLDER.confirmPassword}
            label={setRequiredLabel(FIELD_LABEL.confirmPassword)}
            component={Input}
            type={showPassword ? TEXT : PASSWORD}
            sx={{...formContentStyle.passwordIcon, ...formContentStyle.confirmPassword}}
            InputProps={{
              endAdornment: (
                <InputAdornment position={END}>
                  <IconButton onClick={handleClickShowPassword} edge={END}>
                    {showPassword ? <EyeIcon /> : <EyeSlashIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </>
      )}

      <Field
        id={FIELD.checkbox}
        name={FIELD.checkbox}
        color={PRIMARY}
        label={privacyPolicyTitle}
        component={Checkbox}
        sx={formContentStyle.checkbox}
      />

      <Button onClick={onSubmitClickHandler} disabled={isSubmitting} title={CONTINUE} endIcon={<ArrowIcon />} sx={formContentStyle.button} />
    </Box>
  );
};

export default FormContent;
